import { api } from "../utils/helpers";
import { subscribeCicdEvents } from "../hooks/useCicdEvents";
import i18n from "../i18n";

export const SNAPSHOT_JOBS = "/api/cicd/transport/snapshot-jobs";
export const activeSnapshotJob = job => ["queued", "running", "reconciling"].includes(job?.state);
export const snapshotJobUrl = (spaceId, id) => `/cicd-pipeline?${new URLSearchParams({ spaceId, snapshotJobId: id })}`;
const storageKey = (userId, spaceId) => `snapshot-jobs:${encodeURIComponent(userId || "")}:${encodeURIComponent(spaceId)}`;

// Nur Kennungen speichern. Nutzlast, Parameter und Berechtigungen bleiben auf dem Server.
export function rememberSnapshotJob(userId, spaceId, id) {
  if (!userId || !spaceId || !id) return;
  try {
    const key = storageKey(userId, spaceId), previous = JSON.parse(localStorage.getItem(key) || "[]");
    localStorage.setItem(key, JSON.stringify([...new Set([id, ...(Array.isArray(previous) ? previous.filter(x => typeof x === "string") : [])])].slice(0, 20)));
  } catch { /* Die Serverliste bleibt die Quelle für die Wiederaufnahme. */ }
}

export async function submitSnapshot(body, options = {}) {
  const request = { method: "POST", timeout: 180000, ...options, body: JSON.stringify(body) };
  try { return await api("/api/cicd/transport/snapshot", request); }
  catch (error) {
    if (!body.clientRequestId || options.signal?.aborted
      || ["SNAPSHOT_PD_SCOPE_UNREADABLE", "SNAPSHOT_QUEUE_FULL", "SNAPSHOT_QUEUE_BUSY", "PAKET_EINSTIEG_NICHT_PRUEFBAR"].includes(error.code)
      || (error.status && error.status < 500)) throw error;
    // Eine verlorene Annahmeantwort wiederholt genau denselben Auftrag, keine neue Kennung.
    return api("/api/cicd/transport/snapshot", request);
  }
}

export function waitForSnapshotJob(initial, { spaceId, signal, onJob = () => {} }) {
  return new Promise((resolve, reject) => {
    const jobSpaceId = initial.spaceId || spaceId;
    let stopped = false, running = false, pending = false, close = () => {}, timer;
    const stop = () => { stopped = true; close(); clearInterval(timer); signal?.removeEventListener("abort", abort); };
    const abort = () => { stop(); reject(new DOMException("Aborted", "AbortError")); };
    const accept = job => {
      if (stopped || job?.id !== initial.id || (job.spaceId && job.spaceId !== jobSpaceId)) return;
      onJob(job);
      if (job.state === "done") { stop(); resolve(job.result); }
      else if (["failed", "cancelled"].includes(job.state)) {
        stop();
        const error = job.error || {}, message = error.message || i18n.t(`freigabe:snapshotQueue.${job.state}`);
        reject(Object.assign(new Error(message), { code: error.code || "SNAPSHOT_CANCELLED", status: 409, details: error.details,
          body: { error: message, code: error.code || "SNAPSHOT_CANCELLED" }, snapshotJob: job }));
      }
    };
    const read = async () => {
      if (stopped) return;
      if (running) { pending = true; return; }
      running = true;
      do {
        pending = false;
        try {
          const result = await api(`${SNAPSHOT_JOBS}/${encodeURIComponent(initial.id)}?spaceId=${encodeURIComponent(spaceId)}`, { signal, retry: false });
          accept(result?.job);
        } catch (error) {
          if (!stopped && [401, 403].includes(error.status)) { stop(); reject(error); }
          // Ein Lesefehler ist kein Beleg für einen fehlgeschlagenen Schreibauftrag.
        }
      } while (pending && !stopped);
      running = false;
    };
    signal?.addEventListener("abort", abort, { once: true });
    if (signal?.aborted) { abort(); return; }
    accept(initial);
    if (stopped) return;
    close = subscribeCicdEvents(spaceId, read);
    timer = setInterval(() => { if (document.visibilityState !== "hidden") read(); }, 60000);
    read();
  });
}
